import { z } from "zod";

/**
 * Modos de limite do catálogo de entitlements (API:
 * `apps/api/src/modules/entitlements/catalog/feature-catalog.ts`). Fechado de
 * propósito: `formatLimit` decide o texto comparando o modo com um destes
 * sete valores, e um oitavo modo silencioso formataria errado sem avisar.
 *
 * Diferente de `usageFeatureSchema.mode` (`../storage/account-storage.schema.ts`),
 * que fica `z.string()` solto: aquele schema é o retrato ao vivo da conta,
 * tolerante a um modo novo que a API já manda e o app ainda não conhece — cair
 * aqui derrubaria o snapshot inteiro. Este schema descreve o catálogo de
 * planos que a vitrine usa para decidir o texto exato de cada modo; um modo
 * desconhecido aqui é bug do catálogo, não algo a tolerar em silêncio. Os dois
 * schemas não se referenciam — reconciliados por ficarem deliberadamente
 * diferentes, não por convergirem.
 */
export const entitlementModeSchema = z.enum([
  "balance",
  "monthly",
  "total",
  "perItem",
  "retention",
  "interval",
  "trialDays",
]);
export type EntitlementMode = z.infer<typeof entitlementModeSchema>;

export const resolvedFeatureSchema = z.object({
  enabled: z.boolean(),
  mode: entitlementModeSchema.nullable(),
  /** `null` é ilimitado. `0` é teto real. */
  limit: z.number().nullable(),
  /**
   * Regime durante a janela de teste do plano, quando a feature participa.
   *
   * A vitrine mostra os limites que o plano **declara**, não os de uma conta
   * específica, então aqui a janela nunca está "aberta" ou "fechada" — o que
   * importa é anunciar o que o teste dá.
   */
  trial: z.object({ enabled: z.boolean(), limit: z.number().nullable() }).optional(),
});
export type ResolvedFeature = z.infer<typeof resolvedFeatureSchema>;

/** Mapa completo devolvido pela API: uma entrada por chave do catálogo. */
export const planEntitlementsSchema = z.record(resolvedFeatureSchema);
export type PlanEntitlements = z.infer<typeof planEntitlementsSchema>;

/**
 * Um degrau da escada de upgrade, já normalizado. `price` em reais; `null`
 * quando o plano não expõe valor unitário.
 */
export const upgradeLadderPlanSchema = z.object({
  planId: z.string(),
  planName: z.string(),
  price: z.number().nullable(),
  /** `MONTHLY`, `ANNUAL` ou `null` em linha antiga — `null` lê como mensal. */
  cycle: z.string().nullable(),
  /** Teto que este plano libera para a feature bloqueada. `null` é ilimitado. */
  unlockedValue: z.number().nullable(),
  entitlements: planEntitlementsSchema,
});
export type UpgradeLadderPlan = z.infer<typeof upgradeLadderPlanSchema>;

/**
 * O que o 409/403 de limite de plano carrega, já normalizado para o front.
 *
 * `current`, `limit` e `unlockedValue` são nulos em `FEATURE_DISABLED`: ali não
 * existe teto, a feature inteira está fora do plano. Quem renderiza decide pelo
 * `code`, não pela presença dos números.
 */
export const entitlementErrorSchema = z.object({
  code: z.enum(["LIMIT_REACHED", "FEATURE_DISABLED"]),
  feature: z.string(),
  /** Rótulo da feature vindo do catálogo — "Notas", "Campanhas". */
  label: z.string(),
  current: z.number().nullable(),
  limit: z.number().nullable(),
  planName: z.string(),
  /** Nome do plano que libera mais. `null` quando não há para onde subir. */
  upgradeTo: z.string().nullable(),
  upgradePlanId: z.string().nullable(),
  /** Teto que o plano de destino libera. `null` é ilimitado ou sem destino. */
  unlockedValue: z.number().nullable(),
  /** Planos acima do atual, do mais barato ao mais caro. Vazio quando não há. */
  upgradeLadder: z.array(upgradeLadderPlanSchema),
  /** Limites que a conta tem hoje — a linha de base do primeiro degrau. */
  currentEntitlements: planEntitlementsSchema,
  /**
   * Plano da conta hoje. `null` no grátis — conta ainda sem assinatura, que
   * precisa criar o espaço antes de pagar em vez de trocar de plano.
   */
  currentPlanId: z.string().nullable(),
  message: z.string(),
});
export type EntitlementError = z.infer<typeof entitlementErrorSchema>;
